<template>
  <ContentWrap>
    <el-form :model="query" inline>
      <el-form-item label="商户 ID">
        <el-input v-model="query.merchantId" clearable class="!w-180px" />
      </el-form-item>
      <el-form-item label="酒店名称">
        <el-input v-model="query.hotelName" clearable class="!w-220px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-160px">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-button type="primary" @click="search">查询</el-button>
      <el-button @click="resetQuery">重置</el-button>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="id" label="申请 ID" min-width="170" />
      <el-table-column prop="merchantId" label="商户 ID" min-width="150" />
      <el-table-column prop="hotelName" label="酒店名称" min-width="180" />
      <el-table-column label="业态" width="100">
        <template #default="{ row }">{{ propertyTypeLabel(row.propertyType) }}</template>
      </el-table-column>
      <el-table-column prop="contactName" label="联系人" width="110" />
      <el-table-column prop="contactPhone" label="联系电话" width="145" />
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag :type="statusMeta(row.status).type">{{ statusMeta(row.status).label }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="submitTime" label="提交时间" width="180" />
      <el-table-column label="操作" width="230" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-button
            v-if="row.status === 'PENDING'"
            v-hasPermi="['hotel:platform:onboarding:audit']"
            link
            type="success"
            @click="openAudit(row, true)"
          >
            通过
          </el-button>
          <el-button
            v-if="row.status === 'PENDING'"
            v-hasPermi="['hotel:platform:onboarding:audit']"
            link
            type="danger"
            @click="openAudit(row, false)"
          >
            驳回
          </el-button>
          <el-button
            v-if="row.status === 'FAILED'"
            v-hasPermi="['hotel:platform:onboarding:provision']"
            link
            type="warning"
            @click="retryProvision(row)"
          >
            重试开通
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="loadList"
    />
  </ContentWrap>

  <el-dialog
    v-model="detailVisible"
    title="酒店入驻详情"
    width="760px"
    destroy-on-close
    append-to-body
    class="hotel-onboarding-detail-dialog"
  >
    <div v-loading="detailLoading" class="detail-dialog-body">
      <el-descriptions v-if="detail" :column="2" border>
        <el-descriptions-item label="酒店名称">{{ detail.hotelName }}</el-descriptions-item>
        <el-descriptions-item label="申请状态">
          <el-tag :type="statusMeta(detail.status).type">{{
            statusMeta(detail.status).label
          }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="申请来源">{{
          detail.applicantMemberId ? '酒店移动端会员' : '历史商家端'
        }}</el-descriptions-item>
        <el-descriptions-item label="会员 ID">{{
          detail.applicantMemberId || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="商户 ID">{{
          detail.merchantId || '审核通过后创建'
        }}</el-descriptions-item>
        <el-descriptions-item label="酒店 ID">{{ detail.shopId || '-' }}</el-descriptions-item>
        <el-descriptions-item label="经营主体">{{
          detail.companyName || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="信用代码">{{ detail.licenseNo || '-' }}</el-descriptions-item>
        <el-descriptions-item label="法人/负责人">{{
          detail.legalPerson || '-'
        }}</el-descriptions-item>
        <el-descriptions-item label="联系邮箱">{{
          detail.contactEmail || '-'
        }}</el-descriptions-item>
        <el-descriptions-item v-if="detail.licenseUrl" label="营业执照" :span="2">
          <el-link :href="detail.licenseUrl" target="_blank" type="primary">查看原件</el-link>
        </el-descriptions-item>
        <el-descriptions-item label="酒店业态">{{
          propertyTypeLabel(detail.propertyType)
        }}</el-descriptions-item>
        <el-descriptions-item label="联系人"
          >{{ detail.contactName }} / {{ detail.contactPhone }}</el-descriptions-item
        >
        <el-descriptions-item label="经营地址" :span="2">{{ detail.address }}</el-descriptions-item>
        <el-descriptions-item label="开通次数">{{
          detail.provisionAttempts || 0
        }}</el-descriptions-item>
        <el-descriptions-item label="提交时间">{{ detail.submitTime || '-' }}</el-descriptions-item>
        <el-descriptions-item v-if="detail.lastProvisionError" label="开通失败" :span="2">
          <el-text type="danger">{{ detail.lastProvisionError }}</el-text>
        </el-descriptions-item>
        <el-descriptions-item v-if="detail.rejectReason" label="驳回原因" :span="2">
          <el-text type="danger">{{ detail.rejectReason }}</el-text>
        </el-descriptions-item>
      </el-descriptions>

      <h3>资质原件</h3>
      <el-empty v-if="materials.length === 0" description="没有可展示的资质材料" :image-size="60" />
      <el-table v-else :data="materials" border>
        <el-table-column prop="name" label="材料名称" />
        <el-table-column label="原件" width="100">
          <template #default="{ row }">
            <el-link v-if="safeMaterialUrl(row.url)" :href="row.url" target="_blank" type="primary"
              >查看</el-link
            >
            <el-text v-else type="danger">地址无效</el-text>
          </template>
        </el-table-column>
      </el-table>

      <template v-if="contract">
        <h3>合同与费率</h3>
        <el-descriptions :column="2" border>
          <el-descriptions-item label="合同编号">{{ contract.contractNo }}</el-descriptions-item>
          <el-descriptions-item label="合同版本"
            >V{{ contract.version }} / {{ contract.status }}</el-descriptions-item
          >
          <el-descriptions-item label="经营模式">{{
            operationModeLabel(contract.operationMode)
          }}</el-descriptions-item>
          <el-descriptions-item label="佣金率">{{ contract.commissionRate }}%</el-descriptions-item>
          <el-descriptions-item label="结算周期">{{
            settleCycleLabel(contract.settleCycle)
          }}</el-descriptions-item>
          <el-descriptions-item label="保证金">{{
            contract.depositAmount || 0
          }}</el-descriptions-item>
          <el-descriptions-item label="有效期"
            >{{ contract.effectiveDate }} 至 {{ contract.expiryDate }}</el-descriptions-item
          >
          <el-descriptions-item label="自动续约">{{
            contract.autoRenew ? '是' : '否'
          }}</el-descriptions-item>
          <el-descriptions-item label="合同原件" :span="2">
            <el-link :href="contract.contractFileUrl" target="_blank" type="primary"
              >查看合同</el-link
            >
          </el-descriptions-item>
        </el-descriptions>
      </template>

      <h3>审核与开通日志</h3>
      <el-timeline>
        <el-timeline-item
          v-for="item in logs"
          :key="item.id"
          :timestamp="item.createTime"
          placement="top"
        >
          <el-card shadow="never">
            <div
              >{{ actionLabel(item.action) }}：{{ statusMeta(item.fromStatus as any).label }} →
              {{ statusMeta(item.toStatus as any).label }}</div
            >
            <el-text v-if="item.opinion" type="info">{{ item.opinion }}</el-text>
          </el-card>
        </el-timeline-item>
      </el-timeline>
    </div>
    <template #footer>
      <el-button @click="detailVisible = false">关闭</el-button>
    </template>
  </el-dialog>

  <Dialog
    v-model="auditVisible"
    :title="auditForm.approved ? '审核通过并配置合同' : '驳回酒店入驻申请'"
    width="720px"
  >
    <el-form ref="auditFormRef" :model="auditForm" :rules="auditRules" label-width="110px">
      <el-form-item label="审核意见" prop="opinion">
        <el-input
          v-model="auditForm.opinion"
          type="textarea"
          :rows="3"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>
      <template v-if="auditForm.approved">
        <el-form-item label="合同编号" prop="contractNo"
          ><el-input v-model="auditForm.contractNo"
        /></el-form-item>
        <el-form-item label="合同原件" prop="contractFileUrl">
          <UploadFile
            v-model="auditForm.contractFileUrl"
            :limit="1"
            :file-type="['doc', 'docx', 'pdf', 'jpg', 'jpeg', 'png']"
            :file-size="30"
          />
        </el-form-item>
        <el-form-item label="经营模式" prop="operationMode">
          <el-select
            v-model="auditForm.operationMode"
            class="!w-100%"
            @change="normalizeCommission"
          >
            <el-option
              v-for="item in operationModes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="平台佣金率" prop="commissionRate">
          <el-input-number
            v-model="auditForm.commissionRate"
            :disabled="auditForm.operationMode === 'SELF_OPERATED'"
            :min="0"
            :max="100"
            :precision="2"
          />
          <span class="ml-8px">%</span>
        </el-form-item>
        <el-form-item label="结算周期" prop="settleCycle">
          <el-select v-model="auditForm.settleCycle" class="!w-100%">
            <el-option
              v-for="item in settleCycles"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="合同有效期" prop="dateRange">
          <el-date-picker
            v-model="auditForm.dateRange"
            type="daterange"
            value-format="YYYY-MM-DD"
            class="!w-100%"
          />
        </el-form-item>
        <el-form-item label="保证金">
          <el-input-number v-model="auditForm.depositAmount" :min="0" :precision="2" />
        </el-form-item>
        <el-form-item label="自动续约"><el-switch v-model="auditForm.autoRenew" /></el-form-item>
      </template>
    </el-form>
    <template #footer>
      <el-button @click="auditVisible = false">取消</el-button>
      <el-button
        :type="auditForm.approved ? 'success' : 'danger'"
        :loading="saving"
        @click="submitAudit"
      >
        {{ auditForm.approved ? '通过并开通' : '确认驳回' }}
      </el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import type { FormInstance, FormRules, TagProps } from 'element-plus'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  auditAdminOnboarding,
  getAdminOnboarding,
  getAdminOnboardingAuditLogs,
  getAdminOnboardingContract,
  getAdminOnboardingPage,
  retryAdminOnboardingProvision,
  type HotelContract,
  type HotelOnboardingApplication,
  type HotelOnboardingAuditLog,
  type HotelOnboardingStatus,
  type HotelQualificationMaterial
} from '@/api/hotel/onboarding'

defineOptions({ name: 'HotelPlatformOnboarding' })

const propertyTypes = ['酒店', '民宿', '客栈', '公寓', '青旅', '度假村']
const statusOptions = [
  { value: 'DRAFT', label: '草稿' },
  { value: 'PENDING', label: '审核中' },
  { value: 'APPROVED', label: '已通过' },
  { value: 'REJECTED', label: '已驳回' },
  { value: 'PROVISIONING', label: '开通中' },
  { value: 'ACTIVE', label: '已开通' },
  { value: 'FAILED', label: '开通失败' },
  { value: 'CANCELED', label: '已撤销' }
] as const
const statusTypes: Record<string, TagProps['type']> = {
  DRAFT: 'info',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  PROVISIONING: 'warning',
  ACTIVE: 'success',
  FAILED: 'danger',
  CANCELED: 'info'
}
const operationModes = [
  { value: 'SELF_OPERATED', label: '平台自营' },
  { value: 'PLATFORM_MANAGED', label: '平台托管' },
  { value: 'THIRD_PARTY', label: '第三方入驻' }
]
const settleCycles = [
  { value: 'INSTANT', label: '即时' },
  { value: 'DAILY', label: '日结' },
  { value: 'WEEKLY', label: '周结' },
  { value: 'MONTHLY', label: '月结' }
]

const loading = ref(false)
const saving = ref(false)
const list = ref<HotelOnboardingApplication[]>([])
const total = ref(0)
const query = reactive({ pageNo: 1, pageSize: 10, merchantId: '', hotelName: '', status: '' })
const detailVisible = ref(false)
const detailLoading = ref(false)
const detail = ref<HotelOnboardingApplication>()
const logs = ref<HotelOnboardingAuditLog[]>([])
const contract = ref<HotelContract | null>(null)
const materials = ref<HotelQualificationMaterial[]>([])
const auditVisible = ref(false)
const auditFormRef = ref<FormInstance>()
const auditForm = reactive({
  applicationId: '' as string | number,
  approved: true,
  opinion: '',
  contractNo: '',
  contractFileUrl: '',
  operationMode: 'THIRD_PARTY',
  commissionRate: 10,
  settleCycle: 'WEEKLY',
  dateRange: [] as string[],
  depositAmount: 0,
  autoRenew: false
})
const auditRules: FormRules = {
  opinion: [{ required: true, message: '请输入审核意见', trigger: 'blur' }],
  contractNo: [{ required: true, message: '请输入合同编号', trigger: 'blur' }],
  contractFileUrl: [{ required: true, message: '请上传合同原件', trigger: 'change' }],
  operationMode: [{ required: true, message: '请选择经营模式', trigger: 'change' }],
  commissionRate: [{ required: true, message: '请输入佣金率', trigger: 'blur' }],
  settleCycle: [{ required: true, message: '请选择结算周期', trigger: 'change' }],
  dateRange: [{ required: true, message: '请选择合同有效期', trigger: 'change' }]
}

function statusMeta(status?: HotelOnboardingStatus) {
  const option = statusOptions.find((item) => item.value === status)
  return { label: option?.label || status || '无', type: statusTypes[status || ''] || 'info' }
}
const propertyTypeLabel = (value: number) => propertyTypes[value] || '-'
const operationModeLabel = (value: string) =>
  operationModes.find((item) => item.value === value)?.label || value
const settleCycleLabel = (value: string) =>
  settleCycles.find((item) => item.value === value)?.label || value
const safeMaterialUrl = (value: string) => /^https?:\/\//i.test(value)
const actionLabel = (value: string) =>
  ({
    CREATE: '创建申请',
    UPDATE: '更新资料',
    SUBMIT: '提交审核',
    APPROVE: '审核通过',
    REJECT: '审核驳回',
    PROVISION: '开始开通',
    PROVISION_SUCCESS: '开通成功',
    PROVISION_FAILED: '开通失败',
    CANCEL: '会员撤销',
    ACTIVATE_ACCOUNT: '激活经营账号'
  })[value] || value

function parseMaterials(value?: string) {
  try {
    const parsed = JSON.parse(value || '{}')
    return Array.isArray(parsed.materials) ? parsed.materials : []
  } catch {
    return []
  }
}

async function loadList() {
  loading.value = true
  try {
    const params = { ...query, merchantId: query.merchantId || undefined }
    const data: any = await getAdminOnboardingPage(params)
    list.value = data?.list || []
    total.value = Number(data?.total || 0)
  } finally {
    loading.value = false
  }
}

function search() {
  query.pageNo = 1
  loadList()
}

function resetQuery() {
  Object.assign(query, { pageNo: 1, pageSize: 10, merchantId: '', hotelName: '', status: '' })
  loadList()
}

async function openDetail(row: HotelOnboardingApplication) {
  detailVisible.value = true
  detailLoading.value = true
  try {
    const [application, auditLogs, latestContract] = await Promise.all([
      getAdminOnboarding(row.id),
      getAdminOnboardingAuditLogs(row.id),
      getAdminOnboardingContract(row.id)
    ])
    detail.value = application
    logs.value = auditLogs || []
    contract.value = latestContract || null
    materials.value = parseMaterials(application.qualificationJson)
  } finally {
    detailLoading.value = false
  }
}

function openAudit(row: HotelOnboardingApplication, approved: boolean) {
  Object.assign(auditForm, {
    applicationId: row.id,
    approved,
    opinion: '',
    contractNo: '',
    contractFileUrl: '',
    operationMode: 'THIRD_PARTY',
    commissionRate: 10,
    settleCycle: 'WEEKLY',
    dateRange: [],
    depositAmount: 0,
    autoRenew: false
  })
  auditVisible.value = true
}

function normalizeCommission() {
  if (auditForm.operationMode === 'SELF_OPERATED') auditForm.commissionRate = 0
}

async function submitAudit() {
  await auditFormRef.value?.validate()
  if (auditForm.approved && !auditForm.contractFileUrl) return ElMessage.warning('请上传合同原件')
  saving.value = true
  try {
    await auditAdminOnboarding({
      applicationId: auditForm.applicationId,
      approved: auditForm.approved,
      opinion: auditForm.opinion.trim(),
      ...(auditForm.approved
        ? {
            contractNo: auditForm.contractNo.trim(),
            contractFileUrl: auditForm.contractFileUrl.trim(),
            operationMode: auditForm.operationMode,
            commissionRate: auditForm.commissionRate,
            settleCycle: auditForm.settleCycle,
            effectiveDate: auditForm.dateRange[0],
            expiryDate: auditForm.dateRange[1],
            depositAmount: auditForm.depositAmount,
            autoRenew: auditForm.autoRenew
          }
        : {})
    })
    ElMessage.success(auditForm.approved ? '审核已通过，系统已触发酒店开通' : '申请已驳回')
    auditVisible.value = false
    await loadList()
  } finally {
    saving.value = false
  }
}

async function retryProvision(row: HotelOnboardingApplication) {
  await ElMessageBox.confirm(
    '确认重试该酒店的模块开通吗？底层会按业务主体幂等复用已有资源。',
    '重试确认',
    {
      type: 'warning'
    }
  )
  await retryAdminOnboardingProvision(row.id)
  ElMessage.success('开通重试已执行')
  await loadList()
}

onMounted(loadList)
</script>

<style scoped>
h3 {
  margin: 22px 0 12px;
}

.detail-dialog-body {
  max-height: calc(100vh - 220px);
  overflow-y: auto;
  padding-right: 8px;
}
</style>
